export type FailureStep = 'HTTP' | 'PREPARE' | 'SOCKET' | 'PLAYER' | 'EVENTS' | 'LOBBY' | 'SETTINGS' | 'RECONNECT';

export class RequestFailure extends Error {
  constructor(
    public readonly kind: 'TIMEOUT' | 'CONNECTION' | 'SESSION' | 'CSRF' | 'REJECTED',
    public readonly status?: number
  ) {
    super(kind);
  }
  /** the server's answer of a rejected request (only set by `post(..., { withStatus: true })`) */
  public body?: unknown;
}

export interface FailureDiagnostic {
  code: string;
  step: FailureStep;
  status?: number;
  time: string;
  reference: string;
  message: string;
  canSignIn: boolean;
  clientError?: string;
  clientSource?: string;
  online?: boolean;
  visibility?: string;
}

// No server response bodies, URLs, cookies or player data in screenshot diagnostics.
export function describeFailure(error: unknown, step: FailureStep): FailureDiagnostic {
  const statusValue = (error as { status?: unknown })?.status;
  const status = typeof statusValue === 'number' && Number.isInteger(statusValue) &&
    statusValue >= 0 && statusValue <= 599 ? statusValue : undefined;
  const kind = error instanceof RequestFailure ? error.kind :
    (error as { name?: string })?.name === 'TimeoutError' ? 'TIMEOUT' :
    status === 0 ? 'CONNECTION' : status !== undefined ? 'HTTP' : 'CLIENT';
  const reason = kind === 'HTTP' || kind === 'REJECTED' ? String(status ?? 'UNKNOWN') : kind;
  let message = 'Der Spielbereich konnte nicht vollständig geladen werden. Bitte versuche es erneut. Bleibt der Fehler bestehen, sende uns ein Bildschirmfoto mit dem Fehlercode.';
  if (kind === 'SESSION') message = 'Deine Anmeldung ist abgelaufen. Bitte melde dich erneut an.';
  else if (kind === 'CSRF') message = 'Der Sicherheitsabgleich ist fehlgeschlagen. Bitte lade die Seite erneut.';
  else if (kind === 'CONNECTION' || kind === 'TIMEOUT') message = 'Die Verbindung zum Spielserver konnte nicht rechtzeitig hergestellt werden. Bitte prüfe deine Internetverbindung und versuche es erneut.';
  else if (status === 401) message = 'Die Anmeldung für diese Anfrage wurde nicht erkannt. Bitte lade die Seite erneut; falls nötig, melde dich neu an.';
  else if (status === 403) message = 'Diese Anfrage wurde vom Server abgelehnt. Bitte versuche es erneut und sende uns bei erneutem Auftreten den Fehlercode.';
  else if (status !== undefined && status >= 500) message = 'Der Server konnte diese Anfrage nicht abschließen. Bitte versuche es in Kürze erneut.';
  const time = new Date().toISOString();
  return {
    code: `SP-${step}-${reason}`, step, status, time,
    reference: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`.toUpperCase(),
    message, canSignIn: kind === 'SESSION',
    clientError: ['TypeError', 'RangeError', 'ReferenceError', 'SyntaxError', 'Error'].includes((error as Error)?.name)
      ? (error as Error).name : undefined,
    clientSource: typeof (error as Error)?.stack === 'string'
      ? (error as Error).stack.match(/(?:main|chunk|polyfills|runtime)[.\w-]*\.js:\d+:\d+/)?.[0] : undefined,
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
  };
}

export async function withDeadline<T>(operation: Promise<T>, milliseconds = 15000): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new RequestFailure('TIMEOUT')), milliseconds);
      }),
    ]);
  } finally {
    clearTimeout(timer!);
  }
}

// For reads only. A timeout has an unknown outcome and is deliberately not retried.
export async function retryReadOnce<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await withDeadline(read());
  } catch (error) {
    if (![0, 502, 503, 504].includes((error as { status?: number })?.status!)) throw error;
    await new Promise(resolve => setTimeout(resolve, 750));
    return withDeadline(read());
  }
}
